import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import LanguageToggle from '../components/LanguageToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PricingPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [plans, setPlans] = useState([]);
  const [billingPeriod, setBillingPeriod] = useState('monthly');

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/plans`);
      setPlans(response.data.plans || []);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const getPlansByPeriod = () => {
    if (billingPeriod === 'yearly') {
      return plans.filter(p => p.name.includes('yearly') || p.name === 'free_trial');
    }
    return plans.filter(p => p.name.includes('monthly') || p.name === 'free_trial');
  };

  const getPrice = (plan) => {
    if (plan.name === 'free_trial') return language === 'tr' ? 'Ücretsiz' : 'Free';
    if (billingPeriod === 'yearly') {
      return `₺${plan.price_yearly}/${language === 'tr' ? 'yıl' : 'year'}`;
    }
    return `₺${plan.price_monthly}/${language === 'tr' ? 'ay' : 'month'}`;
  };

  const getDisplayName = (plan) => {
    return language === 'tr' ? plan.display_name_tr : plan.display_name_en;
  };

  const handleSelectPlan = async (plan) => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login', { state: { returnUrl: '/pricing', selectedPlan: plan.name } });
      return;
    }

    // Map plan to package_id
    const packageIdMap = {
      'free_trial': 'free_trial',
      'monthly_student': 'monthly_student',
      'yearly_student': 'yearly_student',
      'monthly_institution': 'monthly_institution',
      'yearly_institution': 'yearly_institution'
    };

    const packageId = packageIdMap[plan.name] || (billingPeriod === 'yearly' ? 'yearly_student' : 'monthly_student');

    try {
      // Get current origin
      const originUrl = window.location.origin;

      // Create checkout session
      const response = await axios.post(
        `${API}/payments/checkout/session`,
        {
          package_id: packageId,
          origin_url: originUrl
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.skip_payment) {
        // Free trial activated
        alert(language === 'tr' ? '✅ Ücretsiz deneme aktif edildi!' : '✅ Free trial activated!');
        navigate('/student/dashboard');
      } else if (response.data.url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert(language === 'tr' ? 'Ödeme başlatılamadı. Lütfen tekrar deneyin.' : 'Failed to initiate payment. Please try again.');
    }
  };

  const featuredPlans = getPlansByPeriod();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
      <LanguageToggle />
      <Navbar title={language === 'tr' ? 'Fiyatlandırma' : 'Pricing'} />

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {language === 'tr' ? '📊 Fiyatlandırma Planları' : '📊 Pricing Plans'}
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            {language === 'tr' 
              ? 'Size en uygun planı seçin. İlk 30 gün ücretsiz!' 
              : 'Choose the plan that fits you best. First 30 days free!'}
          </p>

          <div className="inline-flex bg-white rounded-full p-1 shadow-lg">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`px-6 py-2 rounded-full font-semibold transition-all ${
                billingPeriod === 'monthly'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {language === 'tr' ? 'Aylık' : 'Monthly'}
            </button>
            <button
              onClick={() => setBillingPeriod('yearly')}
              className={`px-6 py-2 rounded-full font-semibold transition-all ${
                billingPeriod === 'yearly'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {language === 'tr' ? 'Yıllık' : 'Yearly'}
              <span className="ml-2 text-xs bg-green-500 text-white px-2 py-1 rounded-full">
                {language === 'tr' ? '2 Ay Bedava' : 'Save 2 Months'}
              </span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {featuredPlans.map((plan) => {
            const isFree = plan.name === 'free_trial';
            const isPopular = plan.name.includes('student');
            
            return (
              <div
                key={plan.id}
                className={`relative bg-white rounded-2xl shadow-xl p-8 ${
                  isPopular ? 'ring-4 ring-blue-500 transform scale-105' : ''
                }`}
              >
                {isPopular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      {language === 'tr' ? '🔥 En Popüler' : '🔥 Most Popular'}
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {getDisplayName(plan)}
                  </h3>
                  <div className="text-4xl font-bold text-blue-600 mb-2">
                    {getPrice(plan)}
                  </div>
                  {plan.trial_days > 0 && (
                    <p className="text-sm text-green-600 font-semibold">
                      {language === 'tr' ? '30 Gün Ücretsiz' : '30 Days Free'}
                    </p>
                  )}
                </div>

                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span className="text-gray-700">
                      {plan.daily_ai_questions} {language === 'tr' ? 'AI soru/gün' : 'AI questions/day'}
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span className="text-gray-700">
                      {plan.monthly_ai_questions} {language === 'tr' ? 'AI soru/ay' : 'AI questions/month'}
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span className="text-gray-700">
                      {plan.daily_mentor_messages} {language === 'tr' ? 'mentor mesaj/gün' : 'mentor messages/day'}
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span className="text-gray-700">
                      {plan.max_booklets} {language === 'tr' ? 'kişisel kitapçık' : 'booklets'}
                    </span>
                  </li>
                  {plan.access_study_planner && (
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-gray-700">
                        {language === 'tr' ? 'Çalışma Planı' : 'Study Planner'}
                      </span>
                    </li>
                  )}
                  {plan.access_detailed_analytics && (
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-gray-700">
                        {language === 'tr' ? 'Detaylı Analiz' : 'Analytics'}
                      </span>
                    </li>
                  )}
                </ul>

                <button
                  onClick={() => handleSelectPlan(plan)}
                  className={`w-full py-3 rounded-xl font-semibold transition-all ${
                    isFree
                      ? 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                      : isPopular
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-purple-600 text-white hover:bg-purple-700'
                  }`}
                >
                  {isFree
                    ? language === 'tr' ? 'Ücretsiz Başla' : 'Start Free'
                    : language === 'tr' ? 'Planı Seç' : 'Choose Plan'}
                </button>
              </div>
            );
          })}
        </div>

        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-3xl p-12 text-white text-center">
          <h2 className="text-3xl font-bold mb-4">
            {language === 'tr' ? '🏫 Okullar ve Kurslar İçin' : '🏫 For Institutions'}
          </h2>
          <p className="text-xl mb-6 opacity-90">
            {language === 'tr' 
              ? 'Kurumunuz için özel fiyatlandırma ve özellikler' 
              : 'Custom pricing and features for your institution'}
          </p>
          <ul className="text-left max-w-2xl mx-auto mb-8 space-y-3">
            <li>✓ {language === 'tr' ? 'Öğrenci başına ₺80/ay' : '₺80/student/month'}</li>
            <li>✓ {language === 'tr' ? 'Toplu öğrenci yönetimi' : 'Bulk student management'}</li>
            <li>✓ {language === 'tr' ? 'Detaylı raporlar' : 'Detailed reports'}</li>
            <li>✓ {language === 'tr' ? 'Özel içerik' : 'Custom content'}</li>
            <li>✓ {language === 'tr' ? 'Öncelikli destek' : 'Priority support'}</li>
          </ul>
          <button
            onClick={() => navigate('/institution-demo')}
            className="bg-white text-indigo-600 px-8 py-4 rounded-xl font-bold text-lg hover:shadow-2xl transition-all"
          >
            {language === 'tr' ? '📞 Demo Talep Et' : '📞 Request Demo'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
